import React, { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Button,
  Modal,
  Radio,
  Select,
  Checkbox,
  Tooltip,
} from "antd";
import {
  programRoles,
  roles,
  legoDragonLevels,
  contentType,
  Data,
} from "./Json";
import "./sesameAccount.scss";
import { FormInstance } from "antd/lib";
import { InfoCircleOutlined } from "@ant-design/icons";
import { isEmpty } from "../../../common/utils/helpers";

const { Option } = Select;

export interface FormData {
  requestType?: string;
  requesterEmail?: string;
  role?: string;
  languageCode?: string;
  affiliation?: string;
  productLine?: string;
  contentType?: string;
  automatedPolyglotFileSharing?: string;
  includedInEmailCommunications?: string;
  fullName?: string;
  city?: string;
  country?: string;
  personalEmail?: string;
  newResourceEmail?: string;
  emailSuspensionReactivation?: string;
  trainingTeamInstructions?: string;
  legoDragonLevel?: string;
  linguistLevel?: string;
}

interface StepProps {
  form: FormInstance<any>;
  languages?: any;
  productLines?: any;
  disableOptions?: any;
  isMenuItem?: boolean;
  buttonLabel?: string;
  isEditingModal?: boolean;
}

const ProgramRole: React.FC<{ roles: Data[]; disabled?: boolean }> = ({
  roles,
  disabled,
}) => {
  return (
    <Form.Item
      label="Program/Project Role"
      name="role"
      rules={[{ required: true, message: "Please select your role!" }]}
    >
      <Radio.Group disabled={disabled}>
        {roles.map((el: Data) => (
          <Form.Item key={el?.key} className="m-0">
            <Radio value={el?.value}>{el?.key}</Radio>
          </Form.Item>
        ))}
      </Radio.Group>
    </Form.Item>
  );
};

interface Language {
  value: string;
  title: string;
}

interface LanguageCodeProps {
  label: string;
  languages: Language[];
  allowSearch?: boolean;
}

const LanguageCode: React.FC<LanguageCodeProps> = ({
  label,
  languages,
  allowSearch = false,
}) => {
  return (
    <Form.Item
      label={label}
      name="languageCode"
      rules={[{ required: true, message: "Please select language code!" }]}
      extra={
        <span className="extraText">
          GDT team members can select GDT option here. If your language code is
          not in the list, you can choose 'Other' and provide your language code
          in the 'Additional instructions for Training Team' field in this form.
        </span>
      }
    >
      <Select
        showSearch={allowSearch}
        placeholder="Select a language"
        filterOption={(input, option) =>
          (option?.children as unknown as string)
            .toLowerCase()
            .startsWith(input.toLowerCase())
        }
      >
        {languages?.slice(1).map((el: any) => (
          <Option key={el?.title} value={el?.value}>
            {el?.title}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

const FullName = () => {
  return (
    <Form.Item
      label="Full Name"
      name="fullName"
      rules={[{ required: true, message: "Please input your full name!" }]}
    >
      <Input />
    </Form.Item>
  );
};

const SesameId = () => {
  return (
    <Form.Item
      label="Sesame ID (Example 015-XX_XXXX@015vendor.com)"
      name="sesameId"
      rules={[
        { required: true },
        {
          pattern: /^015-[a-zA-Z]+_\d{4}@015vendor\.com$/,
          message:
            "Invalid email. Format must be: 015-<letters>_<4digits>@015vendor.com (e.g. 015-es_1234@015vendor.com)",
        },
      ]}
    >
      <Input />
    </Form.Item>
  );
};

const Instructions = () => {
  return (
    <Form.Item
      label="Additional Instructions for Training Team"
      name="trainingTeamInstructions"
    >
      <Input.TextArea />
    </Form.Item>
  );
};

const FileAndEmailSharing = ({
  label,
  showNA = false,
  name,
}: {
  label: string;
  showNA?: boolean;
  name: string;
}) => {
  return (
    <Form.Item
      label={label}
      name={name}
      rules={[
        {
          required: true,
          message: "This field is required!",
        },
      ]}
      extra={
        <span className="extraText">
          GDT team can add list of languages that needs to be auto shared in
          Additional Instructions field below
        </span>
      }
    >
      <Radio.Group>
        <Form.Item className="m-0">
          <Radio value="Yes">Yes</Radio>
        </Form.Item>
        <Form.Item className="m-0">
          <Radio value="No">No</Radio>
        </Form.Item>
        {showNA && (
          <Form.Item className="m-0">
            <Radio value="NA">NA</Radio>
          </Form.Item>
        )}
      </Radio.Group>
    </Form.Item>
  );
};

interface InfoTooltipProps {
  title: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ title }) => {
  return (
    <Tooltip title={title}>
      <InfoCircleOutlined
        style={{ color: "var(--primary-color)", cursor: "pointer" }}
      />
    </Tooltip>
  );
};

const StepOne: React.FC<StepProps> = ({
  form,
  disableOptions,
  isMenuItem,
  buttonLabel,
  isEditingModal,
}) => (
  <Form form={form} layout="vertical">
    <Form.Item
      label="Request Type"
      name="requestType"
      rules={[{ required: true, message: "Please select request type!" }]}
    >
      <Radio.Group>
        <Form.Item className="m-0">
          <Radio value="Creation" disabled={isEditingModal}>
            New Sesame Request
          </Radio>
        </Form.Item>

        <Form.Item className="m-0">
          <Radio
            disabled={isEditingModal || disableOptions?.includes(1)}
            value="Suspension"
          >
            Suspension Request
          </Radio>
          <InfoTooltip title="Suspend account is allowed only when status is CREATED or REACTIVATED, and request type is not already Suspension" />
        </Form.Item>
        <Form.Item className="m-0">
          <Radio
            disabled={isEditingModal || disableOptions?.includes(2)}
            value="Reactivation"
          >
            Reactivation Request
          </Radio>
          <InfoTooltip title="Activate account is allowed only when status is SUSPENDED, and request type is not already Reactivation" />
        </Form.Item>
      </Radio.Group>
    </Form.Item>
    <Form.Item
      label="Requester Email ID"
      name="requesterEmail"
      rules={[
        { required: true, message: "Please input your email!", type: "email" },
      ]}
    >
      <Input disabled={isEditingModal} />
    </Form.Item>
  </Form>
);

const StepTwo: React.FC<StepProps> = ({ form, isEditingModal, isMenuItem }) => {
  const requestType = form.getFieldValue("requestType");

  return (
    <Form form={form} layout="vertical">
      {(requestType === "Suspension" || requestType === "Reactivation") &&
        isMenuItem && <SesameId />}
      {requestType === "Creation" && (
        <ProgramRole roles={roles} disabled={isEditingModal} />
      )}
      {requestType === "Reactivation" && !isMenuItem && (
        <>
          <FileAndEmailSharing
            showNA
            label="Automated file sharing in Polyglot (For Translator/Language Lead/GDT)"
            name="automatedPolyglotFileSharing"
          />
          <FileAndEmailSharing
            showNA
            label="Include in HO emails and other email communications (For Translator/Language Lead/GDT)"
            name="includedInEmailCommunications"
          />
        </>
      )}
    </Form>
  );
};

interface ProductLineSelectorProps
  extends Pick<React.ComponentProps<typeof Checkbox.Group>, "value" | "onChange"> {
  options: { title: string; value: string }[];
}

export const ProductLineSelector: React.FC<ProductLineSelectorProps> = ({
  options,
  value = [],
  onChange,
}) => {
  const allValues = options.map((option) => option.value);
  const selectedCount = allValues.filter((item) => value.includes(item)).length;
  const allSelected = allValues.length > 0 && selectedCount === allValues.length;

  return (
    <>
      <Form.Item className="m-0">
        <Checkbox
          checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected}
          disabled={allValues.length === 0}
          onChange={(event) =>
            onChange?.(event.target.checked ? allValues : [])
          }
        >
          Select all
        </Checkbox>
      </Form.Item>
      <Checkbox.Group value={value} onChange={onChange}>
        {options.map((option) => (
          <Form.Item key={option.title} className="m-0">
            <Checkbox value={option.value}>{option.title}</Checkbox>
          </Form.Item>
        ))}
      </Checkbox.Group>
    </>
  );
};

const StepThree: React.FC<StepProps> = ({ form, languages, productLines }) => {
  const role = form.getFieldValue("role");

  return (
    <Form form={form} layout="vertical">
      {(role === "Linguist" || role === "GDT Lead") && (
        <>
          <Form.Item
            label="Linguist Level"
            name="linguistLevel"
            rules={[
              { required: true, message: "Please select linguist level!" },
            ]}
          >
            <Radio.Group>
              <Form.Item className="m-0">
                <Radio value="Translator">Translator</Radio>
              </Form.Item>
              <Form.Item className="m-0">
                <Radio value="Language_Lead">Language Lead</Radio>
              </Form.Item>
              <Form.Item className="m-0">
                <Radio value="GDT">GDT</Radio>
              </Form.Item>
            </Radio.Group>
          </Form.Item>
          <LanguageCode
            label="Language Code"
            languages={languages}
            allowSearch={true}
          />
          <Form.Item
            label="Affiliation"
            name="affiliation"
            rules={[{ required: true, message: "Please select affiliation!" }]}
          >
            <Radio.Group>
              <Form.Item className="m-0">
                <Radio value="RWS_Internal">RWS Internal</Radio>
              </Form.Item>
              <Form.Item className="m-0">
                <Radio value="External">External</Radio>
              </Form.Item>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Select Product Line"
            name="productLine"
            rules={[
              {
                required: true,
                message: "This field is required!",
              },
            ]}
          >
            <ProductLineSelector options={productLines?.slice(2) ?? []} />
          </Form.Item>
          <Form.Item
            label="Content type"
            name="contentType"
            rules={[
              {
                required: true,
                message: "This field is required!",
              },
            ]}
          >
            <Checkbox.Group>
              {contentType.map((el) => {
                return (
                  <Form.Item key={el?.key} className="m-0">
                    <Checkbox value={el?.value}>{el?.key}</Checkbox>
                  </Form.Item>
                );
              })}
            </Checkbox.Group>
          </Form.Item>
          <FileAndEmailSharing
            label="Automated file sharing in Polyglot"
            name="automatedPolyglotFileSharing"
          />
          <FileAndEmailSharing
            name="includedInEmailCommunications"
            label="Include in HO emails and other email communications"
          />
        </>
      )}
      <FullName />
      {(role === "Tester" ||
        role === "LQE_Subtitling" ||
        role === "LQE_Non_Subtitling") && (
        <>
          <Form.Item
            label="Linguist Level"
            name="linguistLevel"
            rules={[
              {
                required: true,
                message: "This field is required!",
              },
            ]}
            initialValue={role}
          >
            <Radio.Group>
              {role === "Tester" && <Radio value="Tester">Tester</Radio>}
              {role === "LQE_Subtitling" && (
                <Radio value="Lqe_Subtitling">LQE Subtitling</Radio>
              )}
              {role === "LQE_Non_Subtitling" && (
                <Radio value="Lqe_Non_Subtitling">LQE Non Subtitling</Radio>
              )}
            </Radio.Group>
          </Form.Item>

          <LanguageCode
            label="Language Code"
            languages={languages}
            allowSearch={true}
          />
        </>
      )}
      <Form.Item label="City" name="city">
        <Input />
      </Form.Item>
      <Form.Item label="Country" name="country">
        <Input />
      </Form.Item>
      <Form.Item
        label="Email ID of new resource"
        name="newResourceEmail"
        rules={[
          {
            required: true,
            message: "Please input your email ID!",
            type: "email",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Instructions />
    </Form>
  );
};

interface MultiStepFormProps {
  visible: boolean;
  onClose: (resetFields: () => void) => void;
  onSubmit: (data: FormData, resetFields: () => void) => void;
  requesterEmail: string;
  languages: any;
  productLines: any;
  disableOptions?: number[];
  isMenuItem?: boolean;
  initialValues?: any;
  buttonLabel?: string;
  isEditingModal?: boolean;
}

const requestTypeLabels: Record<string, string> = {
  Creation: "New Sesame Request",
  Suspension: "Suspension Request",
  Reactivation: "Reactivation Request",
};

const SesameRequest: React.FC<MultiStepFormProps> = ({
  visible,
  onClose,
  onSubmit,
  requesterEmail,
  languages,
  productLines,
  disableOptions,
  isMenuItem,
  initialValues,
  buttonLabel,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const watchedRequestType = Form.useWatch("requestType", form);
  const [requestType, setRequestType] = useState(watchedRequestType);

  useEffect(() => {
    if (watchedRequestType) {
      setRequestType(watchedRequestType);
    }
  }, [watchedRequestType]);

  const [formData, setFormData] = useState<FormData>({ requesterEmail });

  const isEditingModal = buttonLabel === "Edit";

  useEffect(() => {
    form.setFieldsValue({ requesterEmail, requestType: "Creation" });
  }, [form, requesterEmail]);

  useEffect(() => {
    if (formData.role) {
      const roleToLevel: Record<string, string> = {
        Tester: "Tester",
        LQE_Subtitling: "Lqe_Subtitling",
        LQE_Non_Subtitling: "Lqe_Non_Subtitling",
      };

      const mappedRole = roleToLevel[formData.role];
      if (mappedRole) {
        form.setFieldsValue({ linguistLevel: mappedRole });
      }
    }
  }, [formData.role, form]);

  useEffect(() => {
    if (!isEmpty(initialValues)) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues]);

  const handleNext = async () => {
    try {
      const values = await form.validateFields();
      setFormData((prev) => ({ ...prev, ...values }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const resetFields = () => {
    setCurrentStep(0);
    form.resetFields();
    form.setFieldsValue({ requesterEmail });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let finalData = { ...formData, ...values };

      if (finalData?.linguistLevel === "GDT") {
        finalData = { ...finalData, role: "GDT_Lead" };
      }

      onSubmit(finalData, resetFields);
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const steps = [
    {
      title: "Step 1",
      content: (
        <StepOne
          form={form}
          disableOptions={disableOptions}
          isMenuItem={isMenuItem}
          buttonLabel={buttonLabel}
          isEditingModal={isEditingModal}
        />
      ),
    },
    {
      title: "Step 2",
      content: (
        <StepTwo
          form={form}
          languages={languages}
          isEditingModal={isEditingModal}
          isMenuItem={isMenuItem}
        />
      ),
    },
    {
      title: "Step 3",
      content: (
        <StepThree
          form={form}
          languages={languages}
          productLines={productLines}
        />
      ),
    },
  ];

  const renderButton = useMemo(() => {
    const isSuspension = requestType === "Suspension";
    const isReactivation = requestType === "Reactivation";
    const isLastStep = steps.length > 0 && currentStep === steps.length - 1;

    if (isMenuItem && (isSuspension || isReactivation)) {
      const isFinalStep = currentStep === 1;

      return (
        <>
          {!isFinalStep && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {isFinalStep && (
            <Button type="primary" onClick={handleSubmit}>
              Submit
            </Button>
          )}
        </>
      );
    }

    return (
      <>
        {isSuspension && (
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        )}

        {isReactivation && currentStep === 0 && (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {isReactivation && currentStep === 1 && (
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        )}

        {!isSuspension && !isReactivation && isLastStep && (
          <Button type="primary" onClick={handleSubmit}>
            Submit
          </Button>
        )}
        {!isSuspension && !isReactivation && !isLastStep && (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        )}
      </>
    );
  }, [currentStep, requestType, steps.length, isMenuItem]);

  return (
    <Modal
    title={
    isEditingModal
    ? "Edit Sesame Request"
    : currentStep === 0
    ? "Sesame Request"
    : requestType
    ? `${requestTypeLabels[requestType] || requestType}`
    : "Sesame Request"
}
     
      className="sesame-modal"
      open={visible}
      onCancel={() => onClose(resetFields)}
      maskClosable={false}
      footer={
        <div className="mt-12 ">
          {currentStep > 0 ? <Button onClick={handlePrev}>Back</Button> : null}
          {renderButton}
        </div>
      }
    >
      {steps[currentStep].content}
    </Modal>
  );
};

export default SesameRequest;
